import _get from 'lodash/get'
import toast from '@/services/toast'

import { utc } from 'moment'
export function copy (value) {
  if (Array.isArray(value)) {
    return [].slice.call(value)
  } else if (value instanceof Object) {
    return Object.assign({}, value)
  } else {
    return value
  }
}

/**
 * Creates a case-insensitive sorting function which orders two objects based on the given attributes
 * @param  {[String]} attributes  array of attributes which need to be considered for sorting, e.g. ['name'], leave undefined if you want to compare objects directly (e.g. objects are strings)
 * @return {Function} can be used to put in a `.sort()` function
 */
export function stringSorter (attributes) {
  if (!Array.isArray(attributes)) {
    attributes = [attributes]
  }

  const extract = (object, attribute) => attribute == null ? object : _get(object, attribute)

  const stringSorters = attributes.map((attribute) => (a, b) => {
    const attrA = extract(a, attribute)?.toLocaleLowerCase()
    const attrB = extract(b, attribute)?.toLocaleLowerCase()
    if (attrA < attrB) {
      return -1
    }
    if (attrA > attrB) {
      return 1
    }
    return 0
  })

  return sorter(stringSorters)
}

/**
 * Useful when sorting an array in which some of the items are null. Defaults to sorting the null value to the end of the array.
 * @param [nullLast=true]
 * @returns {function(...[*]=)}
 */
export function nullSorter (nullLast) {
  const multiplier = nullLast === false ? -1 : 1

  return (a, b) => {
    if (a == null && b == null) { return 1 }
    if (a == null) { return multiplier }
    if (b == null) { return -1 * multiplier }

    // both a and b not null
    return 0
  }
}

/**
 * Creates a case-insensitive sorting function which orders two strings
 * @param  {[Boolean]} nullLast decides if null values will be last or first
 * @return {Function} can be used to put in a `.sort()` function
 */
export function stringValueSorter (nullLast = true) {
  const multiplier = nullLast === false ? -1 : 1

  return (a, b) => {
    if (a === null) {
      return multiplier
    } else if (b === null) {
      return -1 * multiplier
    }
    return a.toLocaleLowerCase() > b.toLocaleLowerCase() ? 1 : -1
  }
}

/**
 * Creates a function that sorts an array, based on the sorters
 * A sorter can be just a function, or an object with a func and ascending field
 * If ascending is false, the sorting is reversed to descending
 * @param sorters the array with sort functions or objects
 * @returns {Function} can be used to put in a `.sort()` function
 */
export function sorter (sorters) {
  if (sorters == null) {
    sorters = []
  }
  return (a, b) => {
    for (const sorter of sorters) {
      const ascending = sorter.ascending != null ? sorter.ascending : true
      const func = sorter.func != null ? sorter.func : sorter

      const result = func(a, b)
      if (result !== 0) {
        return ascending ? result : result * -1
      }
    }
    return 0
  }
}

export function orderSorter () {
  return (a, b) => {
    if (a.order < b.order) {
      return -1
    }
    if (a.order > b.order) {
      return 1
    }

    return 0
  }
}

export function studentSortColumns () {
  const sortType = document.head.querySelector('meta[name="setting-student-sort-column"]')?.content
  if (sortType === 'sortFirstName') {
    return ['firstName', 'lastName', 'infixName']
  } else {
    return ['lastName', 'firstName', 'infixName']
  }
}

export function uniqueFilter (value, index, array) {
  return array.indexOf(value) === index
}

/**
 * Generates a deterministic className for a given object. Useful for getting the color for a given Type
 * @param  {String|Int} objectIdentifier    The object identifier for which to generate a class, e.g. '2'
 * @param  {String} prefix    Class prefix, e.g. 'schedule-selector'
 * @param  {[String]} types   [Optional] The array of possible classes, by default the color names
 * @return {String}           A className for the given Object
 */
export function classGenerator (objectIdentifier, prefix, types) {
  const classTypes = types || ['grey', 'yellow', 'green', 'blue', 'purple', 'red']

  if (objectIdentifier == null) {
    return `${prefix}--unknown`
  }

  const type = classTypes[parseInt(objectIdentifier, 10) % classTypes.length]
  return `${prefix}--${type}`
}

/**
 * Defaults to a date
 * @param timestamp
 * @param locale
 * @param format
 * @returns {string}
 */
export function formatTimestamp (timestamp, locale, format) {
  const date = new Date(timestamp * 1000)
  const dateFormat = format || { year: 'numeric', month: 'short', day: 'numeric' }
  if (locale === 'nl-NL') {
    // in IE this dateFormat add dots between the dates resulting in the datestring: 14.mei.2019 instead of 14 mei 2019
    return date.toLocaleDateString(locale, dateFormat).replace(new RegExp('\\.(?! )', 'g'), ' ')
  }

  return date.toLocaleDateString(locale, dateFormat)
}

export function formatTimestampToTime (timestamp, locale, format) {
  const date = new Date(timestamp * 1000)
  const dateFormat = format || {}

  if (locale === 'nl-NL') {
    // in IE this dateFormat add dots between the dates resulting in the datestring: 14.mei.2019 instead of 14 mei 2019
    // IE also doesnt remove leading 0: '07 juni 2019' instead of '7 juni 2019'
    return date.toLocaleDateString(locale, dateFormat).replace(new RegExp('\\.(?! )', 'g'), ' ')
  }

  return date.toLocaleTimeString(locale, dateFormat)
}

/**
 * Formats number fields from HTML number inputs; empty values are removed from the input, string values will be
 * transformed to a float. The changes will be in-place
 *
 * @param data Object of the rule values
 * @param rules Object of the rule definitions
 */
export function formatNumberFields (data, rules) {
  const result = data

  for (const rule of rules) {
    if (rule.type === 'number') {
      if (result[rule.name] === '' || result[rule.name] == null) {
        delete result[rule.name]
      } else {
        result[rule.name] = parseFloat(result[rule.name])
      }
    }
  }
}

/**
 * Decycles the given input (removes cyclic references)
 * Such that it can be used for JSON.stringify
 * @param  {Any} obj
 * @return {Any}
 */
export function decycle (obj) {
  const cache = new Set()

  const replaceObject = obj => {
    const newObj = {}
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        newObj[key] = replacer(obj[key])
      }
    }
    return newObj
  }

  const replacer = value => {
    if (typeof value === 'object' && value != null) {
      if (cache.has(value)) {
        return `[circular ${value.constructor.name.toLowerCase()}]`
      }
      cache.add(value)

      const result = Array.isArray(value)
        ? value.map(replacer)
        : replaceObject(value)

      cache.delete(value)
      return result
    }

    return value
  }

  return replacer(obj)
}

/**
 * Split array into different groups
 * Example split array into odd or even:
 * const { true: even = [], false: odd = [] } = splitArray([1,2,3,4], i => i % 2 == 0)
 * @param  {array} arr
 * @param  {function} splitter  a => groupName|null
 * @return {object} arrays keyed by groupNames
 */
export function splitArray (arr, splitter) {
  const result = {}
  for (const val of arr) {
    const groupName = splitter(val)
    if (groupName != null) {
      if (result[groupName] == null) {
        result[groupName] = []
      }
      result[groupName].push(val)
    }
  }

  return result
}

export function secondsToTimestring (seconds) {
  return utc(seconds * 1000).format('HH:mm')
}

/**
 * Display the float in the correct format
 * Number.EPSILON is necessary due to rounding + floating point errors
 * https://stackoverflow.com/a/11832950
 * @param  {float} float
 * @return {string} string with formatted number
 */
export function displayFloatLocalized (float, locale) {
  return (Math.round((float + Number.EPSILON) * 100) / 100).toLocaleString(locale)
}

export function escapeRegExp (string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') // $& means the whole matched string
}

export function escapeHtml (unsafe) {
  return unsafe
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
}

export function defaultErrorMessage (error) {
  // if has response show the error JSEND
  if (error.response && error.response.data && error.response.data.message) {
    toast.error(error.response.data.message)
  // JSONAPI ERROR
  } else if (error?.response?.data?.errors != null && error?.response?.data?.errors[0] != null && error.response.data.errors[0].details) {
    toast.error(error.response.data.errors[0].details)
  } else {
    toast.error('Unknown error')

    // rethrow non validation errors so that Sentry can capture it
    if (error?.response?.status !== 422) {
      throw error
    }
  }
}
